import { useState, FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Zap, Eye, EyeOff, ArrowLeft, CheckCircle2, KeyRound, Mail } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { authApi, authUrl, resetPassword } from "../lib/api";

/** Fixed messages for the API's /auth/* error codes (?error=<code>). */
const LOGIN_ERRORS: Record<string, string> = {
  no_account:
    "There is no AI Affiliate Stream account for this sign-in. Sign in with the email address your account uses, or ask the account owner.",
  account_conflict: "This account is linked to a different Titanium sign-in. Contact support.",
  ambiguous_account: "This email matches more than one account. Contact support.",
  wrong_app: "That sign-in link is invalid or has expired. Please sign in again.",
  exchange_failed: "That sign-in link is invalid or has expired. Please sign in again.",
  missing_act: "That sign-in link is invalid or has expired. Please sign in again.",
  confirm_expired: "The sign-in confirmation expired. Please sign in again.",
  confirm_failed: "The sign-in confirmation could not be verified. Please sign in again.",
  server_misconfigured: "Sign-in is temporarily unavailable. Please try again later.",
  not_configured: "Sign-in is temporarily unavailable. Please try again later.",
};

export default function Login() {
  const { tenant, config, legacyLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const errorCode = params.get("error");
  const signedOut = params.get("signed_out") === "1";

  // Titanium email link
  const [linkEmail, setLinkEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Reset password state
  const [mode, setMode] = useState<"login" | "reset">("login");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  async function handleSendLink(e: FormEvent) {
    e.preventDefault();
    setLinkError(null);
    setLinkLoading(true);
    try {
      await authApi.sendMagicLink(linkEmail);
      setLinkSent(true);
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      setLinkError(
        status === 429
          ? "Too many sign-in links requested. Please wait a few minutes."
          : status === 400
            ? "Enter a valid email address."
            : "Could not send a sign-in link. Please try again.",
      );
    } finally {
      setLinkLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await legacyLogin(email, password);
      navigate("/");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!currentPassword) {
      setError("Current password is required");
      return;
    }
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email, currentPassword, newPassword);
      setResetSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setLoading(false);
    }
  }

  function switchToLogin() {
    setMode("login");
    setError(null);
    setResetSuccess(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 bg-brand-600 rounded-xl flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-white">AffiliateStream</span>
        </div>

        {tenant && <Navigate to="/" replace />}

        {/* Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 shadow-xl">
          {mode === "login" ? (
            <>
              <h1 className="text-lg font-semibold text-gray-100 mb-1">Sign in to your account</h1>
              <p className="text-sm text-gray-500 mb-6">Welcome back</p>

              {errorCode && (
                <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                  {LOGIN_ERRORS[errorCode] ?? "Sign-in failed. Please try again."}
                </div>
              )}
              {signedOut && !errorCode && (
                <div className="mb-4 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-lg text-sm text-green-400">
                  You are signed out.
                </div>
              )}

              {/* Titanium hosted login: a top-level POST so the API can set its
                  pending-login cookie before redirecting to the hosted page. */}
              <form method="POST" action={authUrl("/auth/start")}>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <KeyRound className="w-4 h-4" />
                  Continue with Titanium
                </button>
              </form>
              <p className="mt-2 text-xs text-gray-500 text-center">Passkey, email link or Google</p>

              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-gray-800" />
                <span className="text-xs text-gray-500">or get a sign-in link by email</span>
                <div className="flex-1 h-px bg-gray-800" />
              </div>

              {linkSent ? (
                <div className="flex flex-col items-center gap-3 py-2 text-center">
                  <Mail className="w-8 h-8 text-indigo-400" />
                  <p className="text-sm text-gray-300">
                    If <span className="font-medium text-gray-100">{linkEmail}</span> can sign in, a link is on its way.
                    Open it in this browser.
                  </p>
                  <button
                    onClick={() => setLinkSent(false)}
                    className="text-xs text-gray-500 hover:text-indigo-400 transition-colors"
                  >
                    Use a different email
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSendLink} className="space-y-3">
                  {linkError && (
                    <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                      {linkError}
                    </div>
                  )}
                  <input
                    type="email"
                    value={linkEmail}
                    onChange={(e) => setLinkEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    aria-label="Email address"
                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                  />
                  <button
                    type="submit"
                    disabled={linkLoading}
                    className="w-full py-2.5 border border-gray-700 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-gray-100 text-sm font-medium rounded-lg transition-colors"
                  >
                    {linkLoading ? "Sending..." : "Email me a sign-in link"}
                  </button>
                </form>
              )}

              {/* LEGACY password login: only when the API runs with LEGACY_PASSWORD_LOGIN=on. */}
              {config.legacyPasswordLogin && (
                <div className="mt-6 pt-6 border-t border-gray-800">
                  {!showLegacy ? (
                    <button
                      onClick={() => setShowLegacy(true)}
                      className="w-full text-xs text-gray-500 hover:text-brand-400 transition-colors"
                    >
                      Sign in with a password (legacy)
                    </button>
                  ) : (
                    <>

              {error && (
                <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full px-3 py-2.5 pr-10 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </button>
              </form>

              <button
                onClick={() => { setMode("reset"); setError(null); }}
                className="w-full mt-4 text-xs text-gray-500 hover:text-brand-400 transition-colors"
              >
                Change password
              </button>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <button
                onClick={switchToLogin}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors mb-4"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to sign in
              </button>

              <h1 className="text-lg font-semibold text-gray-100 mb-1">Change password</h1>
              <p className="text-sm text-gray-500 mb-6">Verify your identity with your current password</p>

              {error && (
                <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                  {error}
                </div>
              )}

              {resetSuccess ? (
                <div className="flex flex-col items-center gap-3 py-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                  <p className="text-sm text-gray-300">Password has been reset.</p>
                  <button
                    onClick={switchToLogin}
                    className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleReset} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Email address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      placeholder="you@example.com"
                      className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Current password
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      New password
                    </label>
                    <div className="relative">
                      <input
                        type={showPw ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        placeholder="••••••••"
                        className="w-full px-3 py-2.5 pr-10 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                      >
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Confirm new password
                    </label>
                    <input
                      type={showPw ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="••••••••"
                      className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {loading ? "Resetting..." : "Reset password"}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">
          AI Affiliate Stream &bull; Dashboard
        </p>
      </div>
    </div>
  );
}
